import {
  extractAxiosErrorMessage,
  useOutreachGmail,
  useOutreachGmailStatus,
} from "@inrange/building-manager-api-client";
import { useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { OutreachGmailConnectButton } from "../leads/OutreachGmailConnect";
import CollapsibleCard from "../site/CollapsibleCard";

// Which mailbox outreach drafts land in, and the connect / disconnect that decides it.
//
// Settings rather than the leads pages because this is where an operator comes to ask
// "who are we sending as?" rather than to act on a lead - the header prompt on /leads
// exists to catch the unconnected case before a run, and deliberately disappears once
// connected, so this is the only place the current account is legible.
const OutreachSendingAccountCard = () => {
  const statusQuery = useOutreachGmailStatus();
  const { disconnectGmail } = useOutreachGmail();
  const [error, setError] = useState<string | null>(null);
  // Set when the disconnect went through on our side but Google's revocation could
  // not be confirmed. Outlives the status flip to "not connected", because that is
  // exactly when the operator would otherwise assume access is gone.
  const [revokeWarning, setRevokeWarning] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const status = statusQuery.data;

  const disconnect = () => {
    setError(null);
    setRevokeWarning(null);
    setConfirming(false);
    disconnectGmail.mutate(undefined, {
      onSuccess: (data) => setRevokeWarning(data.warning),
      onError: async (err) => setError(await extractAxiosErrorMessage(err)),
    });
  };

  return (
    <CollapsibleCard title="Outreach sending account" initialOpen={false}>
      <div style={{ fontSize: 13, display: "grid", gap: 10 }}>
        <div style={{ color: "#666" }}>
          Every outreach email drafted from a lead is written into{" "}
          <strong>this one account&rsquo;s</strong> Gmail drafts folder, whoever
          drafted it. Nothing is ever sent - the account holder reviews each draft
          and sends it by hand.
        </div>

        {statusQuery.isLoading && (
          <div>
            <Spinner animation="border" size="sm" /> Loading...
          </div>
        )}

        {statusQuery.isError && (
          <Alert variant="danger" style={{ fontSize: 13, marginBottom: 0 }}>
            Could not load the sending-account status. Reload to retry.
          </Alert>
        )}

        {status && !status.configured && (
          <Alert variant="secondary" style={{ fontSize: 13, marginBottom: 0 }}>
            Gmail drafting is not configured on this environment, so no account can
            be connected here.
            {(status.configurationGaps ?? []).length > 0 && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {(status.configurationGaps ?? []).map((gap) => (
                  <li key={gap}>{gap}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        {status && status.configured && !status.connected && (
          <div
            style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
          >
            <span>No account is connected, so drafts have nowhere to go.</span>
            <OutreachGmailConnectButton />
          </div>
        )}

        {status && status.connected && (
          <>
            <div>
              Connected as <strong>{status.googleEmail}</strong>
              {status.authorizedBy && <> · authorised by {status.authorizedBy}</>}
              {status.connectedAt && (
                <> · {new Date(status.connectedAt).toLocaleString()}</>
              )}
            </div>
            {status.lastError && (
              <Alert variant="warning" style={{ fontSize: 13, marginBottom: 0 }}>
                {status.lastError}
              </Alert>
            )}
            {confirming ? (
              <Alert variant="warning" style={{ fontSize: 13, marginBottom: 0 }}>
                <div style={{ marginBottom: 8 }}>
                  Disconnecting revokes our access to{" "}
                  <strong>{status.googleEmail}</strong> at Google and deletes the
                  stored credential. Drafts already in that folder stay where they
                  are - we simply stop being able to see or replace them, so a
                  re-draft after reconnecting places a NEW draft rather than
                  replacing the old one.
                </div>
                <Button
                  size="sm"
                  variant="danger"
                  disabled={disconnectGmail.isLoading}
                  onClick={disconnect}
                >
                  {disconnectGmail.isLoading ? "Disconnecting..." : "Yes, disconnect"}
                </Button>{" "}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </Button>
              </Alert>
            ) : (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => setConfirming(true)}
                >
                  Disconnect
                </Button>
                {/* Reconnecting without disconnecting first is the normal way to
                    change account or repair a rejected credential: the callback
                    overwrites the stored grant, so there is no window with nothing
                    connected. */}
                <OutreachGmailConnectButton
                  label="Reconnect / change account"
                  loginHint={status.googleEmail}
                />
              </div>
            )}
          </>
        )}

        {revokeWarning && (
          <Alert
            variant="warning"
            style={{ fontSize: 13, marginBottom: 0 }}
            dismissible
            onClose={() => setRevokeWarning(null)}
          >
            Disconnected here. {revokeWarning} Remove it by hand at{" "}
            <a
              href="https://myaccount.google.com/permissions"
              target="_blank"
              rel="noopener noreferrer"
            >
              myaccount.google.com/permissions
            </a>
            , signed in as the sending account.
          </Alert>
        )}

        {error && (
          <Alert variant="danger" style={{ fontSize: 13, marginBottom: 0 }}>
            {error}
          </Alert>
        )}
      </div>
    </CollapsibleCard>
  );
};

export default OutreachSendingAccountCard;
